import { html, nothing, render } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ColorTypes, SplitContainer, SplitContainerItem, actions, dom, getThemeManager } from "@omegagrid/core";
import { Grid, GridEvent, GridSource, TableTheme } from "@omegagrid/grid";
import type { ThemeEditor } from "@omegagrid/editor";
import { ToolbarItem } from "@omegagrid/toolbar";
import { msg } from "@omegagrid/localize";
import rp, { AppComponent, CodeEditor, Saveable, parseTableTheme, parseTableThemeInput, themeForActiveColorScheme } from "@reporting/rp-app-host";
import { Style as DefStyle } from "@reporting/rp-types/definition";
import { TableStyleCodeEditorAdapter } from "../adapters/general/tableStyleCodeEditorAdapter";
import { DEMO_DATASETS, createDemoSource } from "./data/tableStyleDemoData";
import { panelStyle, style } from "./tableStyleEditor.style";

const PANEL_WIDTH = 320;
/** debounce of the live preview after an edit in the theme editor */
const PREVIEW_DELAY = 300;

/**
 * Editor of a table style (P_STYLE, type T): a demo report grid with the
 * theme applied on the left, the og-editor-theme (or the raw JSON) on the
 * right - the layout of the table view editor in analytics. The demo grid is
 * fully editable, so any row/column/value can be added to try the theme on;
 * the demo edits are kept only for the life of the tab, the save writes the
 * theme definition alone.
 */
@customElement('rp-definition-table-style-editor')
export class TableStyleEditor extends AppComponent implements Saveable {

	static styles = [style];

	/** the edited style row, the theme JSON lives in its style column */
	@property({type: Object})
	styleItem: DefStyle;

	/** working copy of the theme, og-editor-theme mutates it in place */
	@state()
	theme: TableTheme|null = null;

	@state()
	rawMode = false;

	@state()
	datasetId = DEMO_DATASETS[0].id;

	/** row/column names of the last clicked demo cell, for authoring name matchers */
	@state()
	selectedCell: {row?: string, column?: string} = {};

	@query('og-splitcontainer')
	splitContainer: SplitContainer;

	private _dirty = false;
	private _saving = false;
	/** text of a stored style whose JSON does not parse - shown in the raw editor to be fixed */
	private _invalidContent: string|null = null;
	/** demo sources edited by the user, kept per dataset while the editor lives */
	private readonly _editedSources = new Map<string, GridSource>();
	/** stable identity - a new array would re-mount the split container components on every render */
	private readonly _splitItems: SplitContainerItem[] = [{id: 'grid'}, {id: 'panel', size: PANEL_WIDTH}];

	get service() { return rp.ms('definition') }
	get idStyle() { return this.styleItem?.id_style }

	constructor() {
		super();
		// edits of the raw editor bubble as well, the theme editor marks the flag directly
		this.addEventListener('edit', () => this._dirty = true);
	}

	connectedCallback() {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this._onAppThemeChange);
		this.addEventListener('keydown', this._onKeyDown);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		getThemeManager().removeEventListener('change', this._onAppThemeChange);
		this.removeEventListener('keydown', this._onKeyDown);
		window.clearTimeout(this._previewTimer);
	}

	/** the theme of the style row becomes the working copy */
	private loadTheme(item: DefStyle) {
		this._invalidContent = null;
		const content = item.style?.trim();

		if (!content) {
			this.theme = {name: item.description || `${msg('Style')} #${item.id_style}`};
			return;
		}

		let theme: TableTheme|null = null;
		try {
			theme = parseTableTheme(content);
		} catch (e) {
			rp.log.error(e);
		}

		if (theme) {
			this.theme = theme;
		} else {
			// broken JSON: no working theme, the raw editor shows the text to be fixed
			this._invalidContent = item.style;
			this.theme = null;
			this.rawMode = true;
			rp.host.showAlert({type: 'danger', content: msg('Failed to parse style')});
		}
	}

	/** the theme as painted on the demo grid: a copy the model may keep, fixed up for the dark mode */
	private get displayTheme(): TableTheme|undefined {
		return this.theme ? themeForActiveColorScheme(this.theme) : undefined;
	}

	// -- demo grid -----------------------------------------------------------

	private _grid: Grid;
	private get grid() {
		if (this._grid) return this._grid;
		this._grid = dom.createElement<Grid>('og-grid', {
			style: {height: '100%'},
			listeners: [['grid.click', this._onGridClick]],
		});
		return this._grid;
	}

	private loadDataset(id: string, reset = false) {
		if (reset) this._editedSources.delete(id);
		this.datasetId = id;
		const source = this._editedSources.get(id) ?? createDemoSource(id);
		this.grid.setSourceData({...source, theme: this.displayTheme});
		// a dataset opens at its top left corner - the table keeps the offsets of the previous model
		this.grid.updateComplete.then(() => this._grid?.rendering?.scrollTo(0, 0));
	}

	/** switch the dataset keeping the edits of the current one */
	private selectDataset(id: string) {
		if (id == this.datasetId) return;
		if (this._grid?.model) this._editedSources.set(this.datasetId, stripTheme(this._grid.getSourceData()));
		this.loadDataset(id);
	}

	/**
	 * Re-paint the theme: the model is rebuilt from its current source with
	 * the theme artifacts stripped, so rules removed from the theme disappear
	 * as well (a theme applied on top of the previous one would only add). The
	 * user's demo edits and the scroll position survive, the selection does not.
	 */
	private applyTheme() {
		const grid = this._grid;
		if (!grid?.model) return;
		const rowOffset = grid.table?.rowOffset ?? 0;
		const colOffset = grid.table?.colOffset ?? 0;

		grid.setSourceData({...stripTheme(grid.getSourceData()), theme: this.displayTheme});

		if (rowOffset || colOffset) {
			grid.updateComplete.then(() => grid.rendering?.scrollTo(rowOffset, colOffset));
		}
	}

	private _onAppThemeChange = () => this.applyTheme();

	private _onGridClick = (e: GridEvent) => {
		if (!e.cell) return;
		this.selectedCell = {row: e.cell.row?.name, column: e.cell.column?.name};
	}

	// -- theme editors -------------------------------------------------------

	/**
	 * The embedded theme editor, created imperatively and cached - og-* editors
	 * lose their state when lit recreates them. It mutates the theme object in
	 * place and signals the edits via theme.change.
	 */
	private _themeEditor: ThemeEditor;
	private get themeEditor() {
		if (this._themeEditor) return this._themeEditor;
		this._themeEditor = dom.createElement<ThemeEditor>('og-editor-theme', {
			listeners: [['theme.change', this._onThemeEdit]],
		});
		this._themeEditor.style.height = '100%';
		return this._themeEditor;
	}

	private _previewTimer: number;
	private _onThemeEdit = () => {
		this.markDirty();
		window.clearTimeout(this._previewTimer);
		this._previewTimer = window.setTimeout(() => this.applyTheme(), PREVIEW_DELAY);
	}

	/**
	 * The raw JSON editor, cached like the theme editor. Its save/discard
	 * events stop here: the code editor never persists anything, a bubbling
	 * save would clear the unsaved marker of this tab.
	 */
	private _codeEditor: CodeEditor;
	private get codeEditor() {
		if (this._codeEditor) return this._codeEditor;
		this._codeEditor = dom.createElement<CodeEditor>('rp-editor-code', {
			adapter: new TableStyleCodeEditorAdapter(
				this.styleItem,
				() => this.theme,
				() => this._invalidContent,
				theme => this.applyRawTheme(theme),
			),
			listeners: [['save', (e: Event) => e.stopPropagation()]],
		});
		this._codeEditor.style.height = '100%';
		return this._codeEditor;
	}

	/** the parsed raw JSON becomes the working theme (a new identity, the editors follow in updated) */
	private applyRawTheme(theme: TableTheme) {
		this._invalidContent = null;
		this.theme = theme;
		this.markDirty();
	}

	private async setRawMode(raw: boolean) {
		this.rawMode = raw;
		// re-read the working theme, the visual editor may have changed it meanwhile
		// - only after the cached editor is re-attached and rendered
		if (!raw || !this._codeEditor) return;
		await this.updateComplete;
		await this._codeEditor.updateComplete;
		this._codeEditor.discard(true);
	}

	/** unapplied text of the raw editor goes into the working theme; false when it is not a valid theme */
	private applyRawEditor(): boolean {
		const content = this._codeEditor?.editor?.editor?.getValue();
		if (content == null) return true;
		const theme = parseTableThemeInput(content);
		if (!theme) return false;
		this.applyRawTheme(theme);
		return true;
	}

	// -- persistence ---------------------------------------------------------

	private markDirty() {
		this._dirty = true;
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** stores the theme definition only - the demo data never leaves the editor */
	async save() {
		if (this._saving || !this.styleItem) return;
		if (this.rawMode && !this.applyRawEditor()) return;
		if (!this.theme) return;

		this._saving = true;
		this.showLoader();
		try {
			await this.service.patch(`styles/${this.idStyle}/content`, JSON.stringify(this.theme, null, '\t'));
			this.styleItem.style = JSON.stringify(this.theme);
			this._dirty = false;
			// the history dropdown of the raw editor picks up the new version
			this._codeEditor?.historyAdapter?.invalidateItems();
			this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		} catch (error) {
			rp.log.error(error);
			rp.host.showAlert({type: 'danger', content: msg('Error saving theme')});
		} finally {
			this._saving = false;
			this.hideLoader();
		}
	}

	/** drops the working copy and reloads the stored style */
	async discard() {
		if (this._dirty) {
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: `${msg('Discard changes')}?`,
			});
			if (command?.id != 'Y') return;
		}

		this.showLoader();
		try {
			const item = await this.service.get<DefStyle>(`styles/${this.idStyle}`);
			if (!item) return;
			this.styleItem = item;
			this._dirty = false;
			this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		} catch (error) {
			rp.log.error(error);
		} finally {
			this.hideLoader();
		}
	}

	// -- toolbar -------------------------------------------------------------

	private get leftToolbarItems(): ToolbarItem[] {
		return [{
			type: 'button',
			id: 'save',
			text: msg('Save changes'),
			title: '(Ctrl+S)',
			icon: 'save',
			color: ColorTypes.Green,
		}, {
			type: 'button',
			id: 'discard',
			text: msg('Discard changes'),
			icon: 'floppy-disk-circle-xmark',
			color: ColorTypes.Red,
		}, {
			type: 'dropdown',
			id: 'dataset',
			width: 280,
			gap: 10,
			caption: msg('Demo data'),
			value: this.datasetId,
			items: DEMO_DATASETS.map(dataset => ({k: dataset.id, v: dataset.title})),
		}, {
			type: 'button',
			id: 'resetData',
			icon: 'rotate-left',
			color: ColorTypes.Gray,
			title: msg('Reset'),
		}];
	}

	private get rightToolbarItems(): ToolbarItem[] {
		return [{
			type: 'togglebutton',
			id: 'raw',
			icon: 'code',
			color: ColorTypes.Gray,
			title: msg('Raw JSON'),
			value: this.rawMode,
		}];
	}

	private _onToolbarAction = (e: actions.ActionEvent) => {
		switch (e.action.key) {
			case 'save': this.save(); break;
			case 'discard': this.discard(); break;
			case 'dataset': if (e.action.value) this.selectDataset(e.action.value as string); break;
			case 'resetData': this.loadDataset(this.datasetId, true); break;
			case 'raw': this.setRawMode(!!e.action.value); break;
		}
	}

	private _onKeyDown = (e: KeyboardEvent) => {
		if (e.ctrlKey && e.key.toLowerCase() == 's') {
			e.preventDefault();
			this.save();
		}
	}

	// -- rendering -----------------------------------------------------------

	/** the right panel; a plain div the split container mounts, its template is rendered in updated() */
	private _panel: HTMLDivElement;
	private get panel() {
		return this._panel ??= dom.createElement<HTMLDivElement>('div', {className: 'panel'});
	}

	private _createComponent = (id: string) => id == 'grid' ? this.grid : this.panel;

	private _onSplitResize = () => {
		this._grid?.layout();
		this._codeEditor?.layout();
	}

	layout() {
		this.splitContainer?.layout();
		this._grid?.layout();
		this._codeEditor?.layout();
	}

	willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('styleItem') && this.styleItem) this.loadTheme(this.styleItem);
	}

	updated(props: Map<PropertyKey, unknown>) {
		if (!this._grid?.model) {
			this.loadDataset(this.datasetId);
		} else if (props.has('theme') || props.has('styleItem')) {
			this.applyTheme();
			if (this.rawMode) this._codeEditor?.discard(true);
		}

		render(this.renderPanel(), this.panel);

		// keep the widget on the edited object (a new identity rebuilds its
		// lists) - after the render, which is where the widget gets created
		if (this.theme && this._themeEditor && this._themeEditor.theme !== this.theme) {
			this._themeEditor.theme = this.theme;
		}
	}

	private renderPanel = () => html`
		<style>${panelStyle}</style>

		<div class="names">
			<label>${msg('Row')}</label>
			<og-input readonly .value="${this.selectedCell.row ?? ''}"></og-input>
			<label>${msg('Column')}</label>
			<og-input readonly .value="${this.selectedCell.column ?? ''}"></og-input>
		</div>

		<div class="panel-body">
			${this.rawMode ? this.codeEditor : (this.theme ? this.themeEditor : nothing)}
		</div>
	`;

	render = () => html`
		<div class="controls">
			<og-toolbar .items="${this.leftToolbarItems}" @toolbar.action="${this._onToolbarAction}"></og-toolbar>
			<div class="spacer"></div>
			<og-toolbar .items="${this.rightToolbarItems}" @toolbar.action="${this._onToolbarAction}"></og-toolbar>
		</div>

		<div class="body">
			<og-splitcontainer
				orientation="horizontal"
				.items="${this._splitItems}"
				.createComponent="${this._createComponent}"
				@splitContainer.resize="${this._onSplitResize}">
			</og-splitcontainer>
		</div>
	`;

}

/**
 * Drop what the theme painted onto the model, so a changed theme starts from
 * clean demo data: the theme itself, the static styles and the conditional
 * formats (the demo grid has no other source of either).
 */
function stripTheme(source: GridSource): GridSource {
	const clean = (element: {s?: unknown}) => { if (element) delete element.s };
	source.rows?.forEach(clean);
	source.columns?.forEach(clean);
	source.cells?.forEach(clean);
	delete source.styles;
	delete source.rules;
	delete source.theme;
	return source;
}
